import { Button, Divider, Group, IconFont, SegmentedControl, Select } from '@byecode/ui'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { DndContext, MouseSensor, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor, restrictToVerticalAxis } from '@dnd-kit/modifiers'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import type { Field, PageAbstract, TypeInstanceMap } from '@lighthouse/core'
import { ARRANGEMENT } from '@lighthouse/core'
import type { RichTextEditorProps } from '@lighthouse/shared'
import {
    DATE_SYSTEM,
    generateJson,
    getDateVariableOption,
    INNER_TYPE_CANNOT_DISPLAY_AS_TEXT,
    ListItem4ByecodeUi,
    useAtomData
} from '@lighthouse/shared'
import { getRandomIcon, nanoid } from '@lighthouse/tools'
import React, { useCallback, useMemo, useState } from 'react'
import { Controller, useFieldArray, useFormContext } from 'react-hook-form'

import { lastPageOfStackAtom, pageVariableSchemasFamily } from '@/atoms/page/state'
import { AnchorSetting } from '@/components/AnchorSetting'
import { getBlockAndPageDesignLimit } from '@/components/StyleSetting'
import AnimationSetting from '@/components/StyleSetting/AnimationSetting'
import { PositionSetting } from '@/components/StyleSetting/PositionSetting'
import { SizeSetting } from '@/components/StyleSetting/SizeSetting'
import { VisibilitySetting } from '@/components/VisibilitySetting'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'
import { uploadManagerInAppParams } from '@/utils/auth'

import { BlockConfigureSegmentedControl } from '../Common/BlockConfigureSegmentedControl'
import { CursorSetting } from '../Common/CursorSetting'
import type { BlockSettingsSegmentedEnum } from '../constants'
import { BLOCK_SETTINGS_SEGMENTED_ENUM, BLOCK_SIZE_OPTIONS, CARD_BLOCK_DIRECTION, CARD_BLOCK_STYLES, LAYOUT_CONTROL } from '../constants'
import { SortableConfigure } from './SortableConfigure'

interface CardBlockSettingProps {
    allPages: PageAbstract[]
}

const COLS_OPTIONS = Array.from({ length: 5 }).map((_, i) => ({
    label: `${i + 1} 列`,
    value: (i + 1).toString()
}))

const filterField = (arg: Field | TypeInstanceMap) => !INNER_TYPE_CANNOT_DISPLAY_AS_TEXT.has(typeof arg === 'object' ? arg.innerType : arg)

/** 卡片block设置 */
export const CardBlockSetting: React.FC<CardBlockSettingProps> = ({ allPages }) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    const { control, watch } = useFormContext()
    const layout = watch('layout')
    const styleProps = getBlockAndPageDesignLimit('card')
    const {
        fields: rawFields,
        remove,
        move,
        append
    } = useFieldArray({
        control,
        name: 'list',
        keyName: 'key'
    })
    const fields = rawFields as Array<{ key: string; id: string }>

    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: { distance: 8 }
        }),
        useSensor(TouchSensor, {
            activationConstraint: { distance: 8 }
        })
    )

    const [activeId, setActiveId] = useState<string | null>(null)

    const onDragStart = (e: DragStartEvent) => {
        if (e.active) {
            setActiveId(e.active.id as string)
        }
    }

    const onDragEnd = ({ over }: DragEndEvent) => {
        if (over && activeId) {
            const overIndex = fields.findIndex(item => item.id === over.id)
            const activeIndex = fields.findIndex(item => item.id === activeId)
            if (activeIndex !== overIndex) {
                move(activeIndex, overIndex)
            }
        }
    }

    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => s?.pageId || '', [])
    )

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const dateOption = useMemo(() => getDateVariableOption(DATE_SYSTEM), [])
    const { customViewOption } = useVariableCustomViewOption()
    const pageVariableSchemas = useAtomData(pageVariableSchemasFamily(pageId))
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)

    const config: RichTextEditorProps['config'] = useMemo(
        () => ({
            variable: {
                dataSources: dataSourceList,
                pageVariableSchemas,
                viewOptions: customViewOption ? [customViewOption] : undefined,
                pageOptions,
                userOption,
                dateOption
            },
            mode: 'text-style'
        }),
        [customViewOption, dataSourceList, dateOption, pageOptions, pageVariableSchemas, userOption]
    )

    const [currentTab, setCurrentTab] = useState<BlockSettingsSegmentedEnum>(BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN)

    return (
        <>
            <BlockConfigureSegmentedControl isInterAction value={currentTab} onChange={setCurrentTab} />

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.DESIGN && (
                <>
                    <PositionSetting {...styleProps.position} />
                    <Divider />
                    <SizeSetting {...styleProps.size} />
                    <Divider />
                    <Group label="设计">
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <div>布局</div>
                            <Controller
                                control={control}
                                name="layout"
                                render={({ field }) => (
                                    <SegmentedControl {...field} fullWidth data={LAYOUT_CONTROL} style={{ width: 180 }} />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        {layout === ARRANGEMENT.fixed && (
                            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                                <div>每行显示</div>
                                <Controller
                                    control={control}
                                    name="cols"
                                    render={({ field }) => (
                                        <Select
                                            value={field.value?.toString()}
                                            onChange={v => field.onChange(Number(v))}
                                            options={COLS_OPTIONS}
                                            style={{ width: 180 }}
                                        />
                                    )}
                                />
                            </ListItem4ByecodeUi>
                        )}
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <div>排版</div>
                            <Controller
                                control={control}
                                name="direction"
                                render={({ field }) => (
                                    <SegmentedControl {...field} fullWidth data={CARD_BLOCK_DIRECTION} style={{ width: 180 }} />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <div>风格</div>
                            <Controller
                                control={control}
                                name="style"
                                render={({ field }) => (
                                    <SegmentedControl {...field} fullWidth data={CARD_BLOCK_STYLES} style={{ width: 180 }} />
                                )}
                            />
                        </ListItem4ByecodeUi>
                        <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                            <div>尺寸</div>
                            <Controller
                                control={control}
                                name="size"
                                render={({ field }) => (
                                    <SegmentedControl {...field} fullWidth data={BLOCK_SIZE_OPTIONS} style={{ width: 180 }} />
                                )}
                            />
                        </ListItem4ByecodeUi>
                    </Group>
                    <Divider />
                    <Controller control={control} name="cursor" render={({ field }) => <CursorSetting {...field} />} />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.CONFIGURE && (
                <>
                    <Group label="内容">
                        <div style={{ overflowY: 'auto', margin: '8px 0' }}>
                            <DndContext
                                sensors={sensors}
                                modifiers={[restrictToVerticalAxis, restrictToFirstScrollableAncestor]}
                                onDragStart={onDragStart}
                                onDragEnd={onDragEnd}
                                onDragCancel={() => setActiveId(null)}
                            >
                                <SortableContext disabled={disabledWithVersion} items={fields} strategy={verticalListSortingStrategy}>
                                    {fields.map((item, index) => (
                                        <SortableConfigure
                                            key={item.id}
                                            disabled={disabledWithVersion}
                                            config={config}
                                            index={index}
                                            id={item.id}
                                            allPages={allPages}
                                            uploadParams={uploadManagerInAppParams()}
                                            onRemove={remove}
                                        />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        </div>

                        <Button
                            style={{ gap: 6, padding: '0 12px' }}
                            radius={32}
                            onClick={() => {
                                append({
                                    id: nanoid(),
                                    iconColor: '#649BFA',
                                    iconDecoration: { type: 'icon', value: getRandomIcon() },
                                    heading: generateJson('标题'),
                                    introduction: generateJson('请输入描述'),
                                    eventList: []
                                })
                            }}
                            icon={<IconFont size={16} fill="var(--color-gray-500)" type="Add" />}
                        >
                            添加
                        </Button>
                    </Group>

                    <Divider />
                    <AnchorSetting />
                    <Divider />
                    <VisibilitySetting />
                </>
            )}

            {currentTab === BLOCK_SETTINGS_SEGMENTED_ENUM.INTERACTION && <AnimationSetting />}
        </>
    )
}
